import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateStaffSchedule } from "@/hooks/useStaffSchedule";
import { ScheduleForm, StaffOption } from "./ScheduleForm";
import { ScheduleFormData } from "../utils/scheduleSchema";
import { getHttpStatus, getUserFriendlyErrorMessage } from "@/utils/httpErrors";

interface CreateScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  staffOptions: StaffOption[];
}

export const CreateScheduleDialog: React.FC<CreateScheduleDialogProps> = ({
  open,
  onOpenChange,
  hotelId,
  staffOptions,
}) => {
  const { t } = useTranslation();
  const { mutate: createSchedule, isPending } = useCreateStaffSchedule();

  const handleSubmit = (data: ScheduleFormData) => {
    createSchedule(
      {
        staffId: data.staffId,
        hotelId,
        recurrenceType: data.recurrenceType,
        recurrenceInterval: data.recurrenceInterval,
        days: data.days,
        anchorDate: data.anchorDate,
        startTime: data.startTime,
        endTime: data.endTime,
        effectiveFrom: data.effectiveFrom,
        effectiveTo: data.effectiveTo || undefined,
      },
      {
        onSuccess: () => {
          toast.success(t("staffSchedule.create.success"));
          onOpenChange(false);
        },
        onError: (error) => {
          if (getHttpStatus(error) === 409) {
            toast.error(t("staffSchedule.create.conflict"));
          } else {
            toast.error(
              getUserFriendlyErrorMessage(
                error,
                t("staffSchedule.create.error"),
              ),
            );
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("staffSchedule.page.createSchedule")}</DialogTitle>
        </DialogHeader>
        <ScheduleForm
          staffOptions={staffOptions}
          onSubmit={handleSubmit}
          isSubmitting={isPending}
        />
      </DialogContent>
    </Dialog>
  );
};
